import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import UnionSignupLayout, {
  NAVY, AMBER, WHITE, FONT_HEAD, FONT_BODY, StepPills,
} from './UnionSignupLayout';
import ImageCropModal from '../../components/ImageCropModal';
import { unionSlug } from '../../utils/unionProfile';

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE,
};
const sel = { ...inp, cursor: 'pointer' };
const txt = { ...inp, resize: 'vertical', minHeight: '100px', fontFamily: FONT_BODY };
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const fld = { marginBottom: '1.1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.1rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

export default function UnionStep2() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const logoRef  = useRef(null);
  const [logoFile, setLogoFile]       = useState(null);
  const [logoType, setLogoType]       = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [cropFile, setCropFile]       = useState(null);
  const [saving, setSaving]           = useState(false);
  const [form, setForm] = useState({
    phone: '', website: '', address: '', city: 'Chicago', state: 'IL', about: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleLogo = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setCropFile(file);
  };

  const handleLogoCropConfirm = (blob, outputType) => {
    setCropFile(null);
    setLogoFile(blob);
    setLogoType(outputType);
    setLogoPreview(URL.createObjectURL(blob));
  };

  const handleComplete = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) { navigate('/union/signup/step1'); return; }

    setSaving(true);

    let logoUrl = null;
    if (logoFile) {
      const path = `${userId}/logo`;
      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(path, logoFile, { upsert: true, contentType: logoType });
      if (!uploadError) {
        const { data: urlData } = supabase.storage.from('company-logos').getPublicUrl(path);
        logoUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      }
    }

    const metadata = session.user.user_metadata || {};

    const account = {
      id: userId,
      email: session.user.email,
      union_name: metadata.union_name || '',
      local_number: metadata.local_number || '',
      trade: metadata.trade || '',
      phone: form.phone,
      website: form.website,
      address: form.address,
      city: form.city,
      state: form.state,
      about: form.about,
      logo_url: logoUrl,
      // Until now the slug was first written by the dashboard's Save, so a union that never
      // opened the Profile tab had slug = null and no reachable public page at all.
      slug: unionSlug(metadata.union_name, metadata.local_number),
    };
    const { error: accountError } = await supabase.from('union_accounts').upsert(account, { onConflict: 'id' });
    if (accountError && accountError.code === '23505') {
      // Another local with the same name/number already took the slug.
      account.slug = `${account.slug}-${userId.slice(0, 6)}`;
      await supabase.from('union_accounts').upsert(account, { onConflict: 'id' });
    }

    // Gives this union an explicit union_staff row (role: 'owner') -- mirrors EmployerStep2's
    // bootstrap_employer_owner_staff call. Every union-side RLS policy checks is_union_staff(),
    // so without this row the brand-new owner couldn't read or write their own union's data.
    const { error: staffError } = await supabase.rpc('bootstrap_union_owner_staff');
    if (staffError) console.error('[UnionStep2] Could not create owner staff row:', staffError.message);

    setSaving(false);
    navigate('/union/dashboard');
  };

  return (
    <>
    {cropFile && (
      <ImageCropModal
        file={cropFile}
        onCancel={() => setCropFile(null)}
        onConfirm={handleLogoCropConfirm}
      />
    )}
    <UnionSignupLayout
      step={2}
      tipText={t('union.signup.step2.tip')}
      onBack={() => navigate('/union/signup/step1')}
      onContinue={handleComplete}
      continueLabel={saving ? t('union.signup.step2.savingLabel') : t('union.signup.step2.continueLabel')}
    >
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('union.signup.step2.eyebrow')}
      </div>

      <StepPills current={2} />

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('union.signup.step2.title')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {t('union.signup.step2.subtitle')}
        </p>

        {/* Logo upload */}
        <div style={{ ...fld, display: 'flex', alignItems: 'flex-start', gap: '1.25rem' }}>
          <div>
            <div
              onClick={() => logoRef.current.click()}
              style={{
                width: '90px', height: '90px', borderRadius: '50%',
                border: `2px dashed ${logoPreview ? AMBER : '#D0D4DA'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', overflow: 'hidden',
                backgroundColor: 'rgba(13,27,42,0.04)',
                backgroundImage: logoPreview ? `url(${logoPreview})` : 'none',
                backgroundSize: 'cover', backgroundPosition: 'center',
                flexShrink: 0,
              }}
            >
              {!logoPreview && (
                <div style={{ textAlign: 'center', color: 'rgba(13,27,42,0.35)' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <div style={{ fontSize: '0.65rem', marginTop: '3px', fontWeight: 600 }}>{t('union.signup.step2.addLogo')}</div>
                </div>
              )}
            </div>
            <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogo} />
          </div>
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '4px' }}>
              {t('union.signup.step2.logoLabel')} <span style={{ fontWeight: 400, color: 'rgba(13,27,42,0.45)' }}>{t('union.signup.step1.optional')}</span>
            </div>
            <div style={{
              backgroundColor: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.3)',
              borderRadius: '8px', padding: '9px 12px',
              fontSize: '0.8rem', color: NAVY, lineHeight: 1.5,
            }}>
              {t('union.signup.step2.logoHint')}
            </div>
          </div>
        </div>

        {/* Phone + Website */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('union.signup.step2.phoneLabel')} <span style={{ fontWeight: 400, color: 'rgba(13,27,42,0.45)' }}>{t('union.signup.step1.optional')}</span></label>
            <input style={inp} type="tel" placeholder={t('union.signup.step2.phonePlaceholder')} value={form.phone} onChange={e => set('phone', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('union.signup.step2.websiteLabel')} <span style={{ fontWeight: 400, color: 'rgba(13,27,42,0.45)' }}>{t('union.signup.step1.optional')}</span></label>
            <input style={inp} placeholder={t('union.signup.step2.websitePlaceholder')} value={form.website} onChange={e => set('website', e.target.value)} />
          </div>
        </div>

        {/* Address */}
        <div style={fld}>
          <label style={lbl}>{t('union.signup.step2.addressLabel')} <span style={{ fontWeight: 400, color: 'rgba(13,27,42,0.45)' }}>{t('union.signup.step1.optional')}</span></label>
          <input style={inp} placeholder={t('union.signup.step2.addressPlaceholder')} value={form.address} onChange={e => set('address', e.target.value)} />
        </div>

        {/* City + State */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('union.signup.step2.cityLabel')}</label>
            <input style={inp} placeholder="Chicago" value={form.city} onChange={e => set('city', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('union.signup.step2.stateLabel')}</label>
            <select style={sel} value={form.state} onChange={e => set('state', e.target.value)}>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="WI">Wisconsin</option>
              <option value="MI">Michigan</option>
              <option value="OH">Ohio</option>
              <option value="MO">Missouri</option>
              <option value="IA">Iowa</option>
              <option value="MN">Minnesota</option>
            </select>
          </div>
        </div>

        {/* About */}
        <div style={fld}>
          <label style={lbl}>{t('union.signup.step2.aboutLabel')} <span style={{ fontWeight: 400, color: 'rgba(13,27,42,0.45)' }}>{t('union.signup.step1.optional')}</span></label>
          <textarea style={txt} placeholder={t('union.signup.step2.aboutPlaceholder')} value={form.about} onChange={e => set('about', e.target.value)} />
        </div>
      </div>
    </UnionSignupLayout>
    </>
  );
}
